import type { WorkflowActionBinding } from '@erp/workflows';

export interface WorkflowActionChoice {
  workflowId: string;
  name: string;
  description?: string;
}

export type WorkflowActionAvailability =
  'available' | 'unavailable' | 'pending';

export interface WorkflowActionRow {
  workflowId: string;
  name: string | null;
  description?: string;
  availability: WorkflowActionAvailability;
}

/**
 * Rows already attached to the agent, plus the published actions still
 * available to add. A binding whose workflow left the published list stays
 * visible so it can be removed. Until the catalog has loaded, saved bindings
 * stay pending so a slow or failed request does not label them unavailable.
 */
export function workflowActionRows(
  bindings: WorkflowActionBinding[],
  choices: WorkflowActionChoice[],
  options?: { catalogResolved?: boolean },
): { rows: WorkflowActionRow[]; available: WorkflowActionChoice[] } {
  const catalogResolved = options?.catalogResolved ?? true;
  const byId = new Map(choices.map((choice) => [choice.workflowId, choice]));
  const bound = new Set(bindings.map((binding) => binding.workflowId));
  return {
    rows: bindings.map((binding) => {
      if (!catalogResolved) {
        return {
          workflowId: binding.workflowId,
          name: null,
          availability: 'pending',
        };
      }
      const choice = byId.get(binding.workflowId);
      if (!choice) {
        return {
          workflowId: binding.workflowId,
          name: null,
          availability: 'unavailable',
        };
      }
      return {
        workflowId: binding.workflowId,
        name: choice.name,
        description: choice.description,
        availability: 'available',
      };
    }),
    available: choices.filter((choice) => !bound.has(choice.workflowId)),
  };
}
